import * as THREE from 'three';
import { WallElement } from './types';
import { SolidKernel } from './SolidKernel';

// BIM-like Layer Function Priorities. Lower number = Higher Priority (Stronger).
// Structure cuts Finish.
export const LAYER_PRIORITIES: Record<string, number> = {
    'Structure': 1,
    'Substrate': 2,
    'Thermal/Air Layer': 3,
    'Finish 1': 4,
    'Finish 2': 5,
    'Membrane Layer': 0 // Usually zero thickness, ignored for geometry
};

export type JoinType = 'Miter' | 'Butt' | 'SquareOff';

export class WallJoinSolver {

    /**
     * Compute the joined layer profiles for a wall.
     * Returns an array of polygons, one for each layer.
     */
    static computeLayerProfiles(
        wall: WallElement,
        connectedWalls: WallElement[]
    ): [number, number][][] {
        const layers = wall.geometryData.layers;
        if (!layers || layers.length === 0) return [];

        // 1. Calculate offsets for each layer relative to the Wall's Location Line
        // We assume the stored start/end points track the *Location Line*.
        const layerOffsets = this.calculateLayerOffsets(wall);

        // 2. Compute base polygons for each layer (unjoined)
        const basePolygons = layers.map((_layer, index) => {
            return this.createBaseLayerPolygon(wall, layerOffsets[index].startOffset, layerOffsets[index].endOffset);
        });

        // 3. Process Joins for each layer
        // We iterate through connections and modify the polygon vertices
        const contentPolygons = basePolygons.map((poly, layerIndex) => {
            const myLayer = layers[layerIndex];
            const myPriority = LAYER_PRIORITIES[myLayer.function] || 5;

            // Apply Start Joins
            this.applyJoinsAtEnd(poly, wall, connectedWalls, true, myPriority);

            // Apply End Joins
            this.applyJoinsAtEnd(poly, wall, connectedWalls, false, myPriority);

            return poly.map(p => [p.x, p.y] as [number, number]);
        });

        return contentPolygons;
    }

    /**
     * Compute the overall wall profile (outer boundary) with joins applied.
     * Returns 2D polygon points representing the wall's footprint.
     */
    static computeWallProfile(
        wall: WallElement,
        connectedWalls: WallElement[]
    ): [number, number][] {
        // Compute layer profiles and return the outermost one (or combine them)
        const layerProfiles = this.computeLayerProfiles(wall, connectedWalls);
        // Return the first layer profile as the overall profile (typically the exterior finish)
        return layerProfiles.length > 0 ? layerProfiles[0] : [];
    }

    /**
     * Calculate start/end offsets from the location line for each layer.
     * Postive offset = Left of location line (when facing End from Start).
     */
    private static calculateLayerOffsets(wall: WallElement): { startOffset: number, endOffset: number }[] {
        const layers = wall.geometryData.layers;
        const totalThickness = wall.geometryData.thickness;

        // Calculate Center of Core or Wall
        let centerPos = 0;

        if (wall.geometryData.locationLine === 'WallCenterline') {
            centerPos = totalThickness / 2;
        } else if (wall.geometryData.locationLine === 'FinishFaceExterior') {
            centerPos = 0;
        } else if (wall.geometryData.locationLine === 'FinishFaceInterior') {
            centerPos = totalThickness;
        } else if (wall.geometryData.locationLine === 'CoreFaceExterior') {
            // Sum thickness until core
            let sum = 0;
            for (let i = 0; i < layers.length; i++) {
                if (layers[i].function === 'Structure') break;
                sum += layers[i].thickness;
            }
            centerPos = sum;
        } else if (wall.geometryData.locationLine === 'CoreCenterline') {
            // Sum thickness until core + half core
            let sum = 0;
            let coreThick = 0;
            let foundCore = false;
            for (let i = 0; i < layers.length; i++) {
                if (layers[i].function === 'Structure') {
                    coreThick = layers[i].thickness;
                    foundCore = true;
                    break;
                }
                sum += layers[i].thickness;
            }
            centerPos = foundCore ? sum + coreThick / 2 : totalThickness / 2;
        }

        // Generate offsets
        // Layer 0 starts at 0 (relative to Ext Face) -> Offset = 0 - centerPos
        // Layer 0 ends at L0_Thick -> Offset = L0_Thick - centerPos
        const results = [];
        let cursor = 0;
        for (const layer of layers) {
            const startDist = cursor;
            const endDist = cursor + layer.thickness;

            // In our coordinate system (Left is Positive), 
            // Exterior Face is at +Offset? No.
            // Vector logic:
            // Dir = End - Start
            // Perp = {-dy, dx} (Left)
            // If we want offsets to move Left, and layers are Ext->Int...
            // Usually Ext is "Left" or "Top".
            // Let's assume Layer 0 is Left-most (Positive Offset).

            // If WallCenterline is 0. 
            // TotalThick = 0.2. Center = 0.1 from Ext.
            // Layer 0 (0.1 thick): Ext Face is at +0.1?
            // Let's define: P0 = Center + Offset * Perp.

            // If layers are ordered Top to Bottom (Left to Right relative to path):
            // Layer 0 is Left-most.
            // Distance from Left Face to Location Line = centerPos.
            // Layer 0 Start (Left Face) = centerPos distance from Location Line.
            // Coordinate: +centerPos
            // Layer 0 End (Right Face) = centerPos - thickness

            const sOff = centerPos - startDist; // Left face of layer
            const eOff = centerPos - endDist;   // Right face of layer

            results.push({ startOffset: sOff, endOffset: eOff });
            cursor += layer.thickness;
        }

        return results;
    }

    private static createBaseLayerPolygon(wall: WallElement, leftOffset: number, rightOffset: number): THREE.Vector3[] {
        const start = new THREE.Vector3(...wall.geometryData.start);
        const end = new THREE.Vector3(...wall.geometryData.end); // Z=0 usually

        const dir = end.clone().sub(start).normalize();
        const perp = new THREE.Vector3(-dir.y, dir.x, 0); // Left vector

        // P0: Start-Left, P1: End-Left, P2: End-Right, P3: Start-Right
        const p0 = start.clone().add(perp.clone().multiplyScalar(leftOffset));
        const p1 = end.clone().add(perp.clone().multiplyScalar(leftOffset));
        const p2 = end.clone().add(perp.clone().multiplyScalar(rightOffset));
        const p3 = start.clone().add(perp.clone().multiplyScalar(rightOffset));

        return [p0, p1, p2, p3];
    }

    private static applyJoinsAtEnd(
        poly: THREE.Vector3[],
        wall: WallElement,
        connectedWalls: WallElement[],
        isStart: boolean,
        myPriority: number
    ) {
        const myPoint = isStart ? new THREE.Vector3(...wall.geometryData.start) : new THREE.Vector3(...wall.geometryData.end);
        const myDir = isStart
            ? new THREE.Vector3(...wall.geometryData.end).sub(myPoint).normalize()
            : new THREE.Vector3(...wall.geometryData.start).sub(myPoint).normalize();

        // Find connected walls at this end
        const joins = connectedWalls.filter(w => {
            const s = new THREE.Vector3(...w.geometryData.start);
            const e = new THREE.Vector3(...w.geometryData.end);
            return s.distanceTo(myPoint) < 0.01 || e.distanceTo(myPoint) < 0.01;
        });

        if (joins.length === 0) {
            // Check for T-Junctions (Connection to Body of another wall)
            // We only check if WE are the Stem (our endpoint touches their body)
            const tJoins = connectedWalls.filter(w => {
                const s = new THREE.Vector3(...w.geometryData.start);
                const e = new THREE.Vector3(...w.geometryData.end);
                // Check if myPoint lies on segment s-e
                const vec = e.clone().sub(s);
                const len = vec.length();
                if (len < 0.001) return false;

                const dir = vec.normalize();
                const toPoint = myPoint.clone().sub(s);
                const dot = toPoint.dot(dir);

                // Projected point
                const proj = s.clone().add(dir.multiplyScalar(dot));
                const distToLine = proj.distanceTo(myPoint);

                // Check collinearity and bounds
                return distToLine < 0.01 && dot > 0.01 && dot < len - 0.01;
            });

            if (tJoins.length > 0) {
                this.applyTJoin(poly, wall, tJoins[0], isStart, myPriority, myPoint, myDir);
            }
            return;
        }

        // Simplification: Handle 1 connection for now (most common)
        // If L connection
        const otherWall = joins[0];

        const otherS = new THREE.Vector3(...otherWall.geometryData.start);
        const otherIsStart = otherS.distanceTo(myPoint) < 0.01;
        const otherEnd = otherIsStart ? new THREE.Vector3(...otherWall.geometryData.end) : otherS;
        const otherDir = otherEnd.clone().sub(myPoint).normalize();
        // Check configuration
        const joinConfig = wall.geometryData.joinConfiguration;
        const allowJoin = joinConfig ? (isStart ? joinConfig.startAllowJoin : joinConfig.endAllowJoin) : true;
        const joinType = joinConfig ? (isStart ? joinConfig.startJoinType : joinConfig.endJoinType) : 'Butt';

        if (!allowJoin) return; // Skip if Disallow Join is set

        if (joinType === 'SquareOff') {
            // Explicit square-off: butt the end perpendicular to the wall axis
            this.applySquareOff(poly, myPoint, myDir, otherDir, isStart);
        } else {
            // BIM's default corner join is "Clean" (miter): the shared
            // endpoint sits on the centerline intersection, so both walls are
            // cut along the angle bisector. This is what closes the joint with
            // a sharp tip even at acute angles — square-off there would leave
            // a visible V-notch between the two perpendicular end faces.
            this.applyMiter(poly, myPoint, myDir, otherDir, isStart);
        }
    }

    private static applyMiter(
        poly: THREE.Vector3[],
        joinPoint: THREE.Vector3,
        myDir: THREE.Vector3,
        otherDir: THREE.Vector3,
        isStart: boolean
    ) {
        // Indices for the end:
        // If Start: P0 (Left), P3 (Right)
        // If End: P1 (Left), P2 (Right)

        // If Cross > 0 (Left Turn), Inner Angle is Left side?
        // Wait: MyDir is AWAY from join. OtherDir is AWAY from join.
        // My = (1,0), Other = (0,1). Angle = 90.
        // Cross = 1*1 - 0 = 1. Left Turn.
        // Inner corner is between My Left and Other Right? No.

        // Let's use simple logic: Intersect lines.
        // Miter Plane Normal = (MyDir + OtherDir).normalize() ?
        // Bisector = (MyDir + OtherDir).

        // We just need to find the intersection of the two outer edge lines.
        // Only the OUTER edge of the turn needs to move/extend.
        // The INNER edge usually meets perfectly if thicknesses match, or needs simple intersection.

        // Vertices to modify
        const leftIdx = isStart ? 0 : 1;
        const rightIdx = isStart ? 3 : 2;

        // We really need the other wall's edge lines to intersect against.
        // But we don't have the other wall's polygon here easily without recalculating it.
        // Shortcut: Miter Plane.
        // The Miter Plane passes through JoinPoint.
        // Normal = (MyDir - OtherDir) ???
        // Bisector vector B = (MyDir + OtherDir).
        // The Miter Plane is perpendicular to the Bisector?
        // No, the Miter Plane CONTAINS the Bisector.
        // The cut line is the Bisector.

        // If walls are same thickness, bisector works.
        // If different, we need true intersection.
        // Let's compute true intersection with a theoretical miter line.

        // Degenerate straight-angle case (walls collinear, dirs opposing):
        // the sum collapses to zero and normalize() yields (0,0,0), which
        // intersectRayLine below rejects as parallel — the join is simply
        // skipped, which is correct for walls forming a straight line.
        const bisector = myDir.clone().add(otherDir).normalize();

        // Project points onto the line defined by JoinPoint + Bisector?

        // Intersection of (Point + Dir) and (JoinPoint + Bisector)

        // Left Side Line: Origin = Poly[leftIdx], Dir = MyDir
        // Bisector Line: Origin = JoinPoint, Dir = Bisector

        const newLeft = this.intersectRayLine(poly[leftIdx], myDir, joinPoint, bisector);
        const newRight = this.intersectRayLine(poly[rightIdx], myDir, joinPoint, bisector);

        if (newLeft) poly[leftIdx] = newLeft;
        if (newRight) poly[rightIdx] = newRight;
    }

    private static applySquareOff(
        poly: THREE.Vector3[],
        joinPoint: THREE.Vector3,
        myDir: THREE.Vector3,
        otherDir: THREE.Vector3,
        isStart: boolean
    ) {
        // Square Off logic:
        // Cut the polygon with a plane Perpendicular to MyDir, passing through the Intersection Point.
        // Intersection Point: 'joinPoint' is roughly the Location Line intersection.
        // We want to extend/cut 'poly' so it ends squarely at 'joinPoint' projected depth?

        // Actually, for a Join, we usually want to extend TO the intersection of the edges.
        // If Square Off, we extend to the FARTHEST intersection, then square off?
        // Or simply: Extend center to intersect, then draw perp line.

        // Let's implement simpler "Extend to Plane" logic.
        // Plane Origin: joinPoint. Plane Normal: myDir.

        // Wait, if we just cut at joinPoint, we might lose the corner material if walls are thick.
        // Usually, Square Off means:
        // Case 1: L-Corner. Inner corner meets. Outer corner is squared off at the Farthest extension?
        // If Wall A and B meet at 90 deg.
        // A extends to B's outer face. B stops at A's inner face. (Butt)
        // This is strictly a boolean subtract/union.

        // For polygon approximation without full boolean:
        // intersect lines.
        // If Square Off, we don't slant the end. We keep it perp to myDir.
        // We move the end line (P0-P3 or P1-P2) along myDir until it hits... what?
        // It hits the "Limit" line of the other wall.

        // We need the "Other Wall's Edge" that limits us.
        // Since we don't have it easily, let's look at Miter line.
        // The Miter line is the limit for Miter.
        // For Butt/Square, the limit is the Other Wall's Face.

        // Approximation:
        // Calculate Intersection of My Centerline and Other Centerline -> JoinPoint.
        // We want to stop at JoinPoint? No, that leaves a gap at outer corner.

        // Let's stick with Miter for "Clean" join default, and SquareOff only if explicitly requested?
        // Or better: SquareOff implementation:
        // Find intersection of My Outer Edge and Other Outer Edge.
        // Project that intersection onto My Centerline. That is the depth.
        // Cut/Extend to that depth.

        const bisector = myDir.clone().add(otherDir).normalize();

        // 1. Find Outer Intersection (approximate via Bisector logic or Ray intersection)
        // Miter point is the corner.
        // Let's find "Miter Point" for the Outer Edge.
        const leftIdx = isStart ? 0 : 1;
        const rightIdx = isStart ? 3 : 2; // Fixed: 3 is start-right, 2 is end-right in my convention?
        // P0(StartL), P1(EndL), P2(EndR), P3(StartR)
        // If Start: Left=0, Right=3.

        // Outer edge depends on turn direction.
        // Cross product logic (both myDir and otherDir point AWAY from the
        // joint, so the turn sign is inverted relative to travel direction):
        const cross = myDir.x * otherDir.y - myDir.y * otherDir.x;
        // With away-from-joint dirs, cross < 0 means the other wall leaves
        // the joint to my forward-left, so MY LEFT edge is the inner one and
        // the right edge wraps the outer corner (and vice versa).
        const isLeftTurn = cross < 0;
        // If Left Turn (travel-relative), Right Edge is Outer. Left Edge is Inner.

        const outerIdx = isLeftTurn ? rightIdx : leftIdx;
        const innerIdx = isLeftTurn ? leftIdx : rightIdx;

        // Intersection of Outer Edge Ray with... Bisector? 
        // No, with Other Wall's Outer Edge.
        // We simulate Other Wall's Outer Edge by knowing it is parallel to OtherDir.
        // And it passes through a point? 
        // It passes through JoinPoint + Offset.
        // But we don't know Other's thickness easily here?
        // We can assume same thickness for a decent guess, or use Miter point.

        // Let's use the Miter logic to find the "Corner Point".
        // Then we Square Off from there.
        // Corner Point = Intersection of MyOuterRay and BisectorLine.

        const cornerPoint = this.intersectRayLine(poly[outerIdx], myDir, joinPoint, bisector);

        if (!cornerPoint) return; // Parallel or error

        // 2. Project Corner Point onto My Centerline (or simply use it as depth reference)
        // Plane defining the end: Passes through CornerPoint. Normal = myDir.

        // 3. Project Inner Point to this plane
        // New Inner = Intersection of InnerRay and Plane.
        // Ray: poly[innerIdx], myDir. 
        // Line is parallel to Normal? Yes.
        // So simply: Inner Point is projected to be collinear with Outer Point relative to Cross axis.
        // Basically, take Inner Point, move it along myDir until it aligns with CornerPoint.

        // Dist to move = (CornerPoint - InnerPoint) dot myDir
        const dist = cornerPoint.clone().sub(poly[innerIdx]).dot(myDir);
        const newInner = poly[innerIdx].clone().add(myDir.clone().multiplyScalar(dist));

        // Update Poly
        poly[outerIdx] = cornerPoint;
        poly[innerIdx] = newInner;
    }

    private static applyTJoin(
        poly: THREE.Vector3[],
        myWall: WallElement,
        targetWall: WallElement,
        isStart: boolean,
        myPriority: number,
        myPoint: THREE.Vector3,
        myDir: THREE.Vector3
    ) {
        // T-Junction Logic:
        // 'targetWall' is the Head. 'myWall' is the Stem.
        // We need to extend 'poly' to meet the appropriate layer of 'targetWall'.

        // 1. Get Target Wall Geometry
        const tStart = new THREE.Vector3(...targetWall.geometryData.start);
        const tEnd = new THREE.Vector3(...targetWall.geometryData.end);
        const tDir = tEnd.clone().sub(tStart).normalize();

        // 2. Identify Target Layer Face depth
        // If I am Core (1), I go to Target Core Center (or Far Face).
        // If I am Finish (>1), I go to Target Finish Face.

        // Simplified: 
        // Calculate distance from Target Location Line to the Face we want to hit.
        // Target Location Line is tStart -> tEnd.
        // My vector 'myDir' intersects it roughly perpendicular.

        // Which side of Target Wall am I on?
        // Cross product of tDir and (MyPoint - tStart).
        const toMe = myPoint.clone().sub(tStart);
        const cross = tDir.clone().cross(toMe).z;
        const isLeft = cross > 0; // Assuming 2D logic

        // Target Wall Thickness breakdown
        // We reuse logic from calculateLayerOffsets but simplified for target core availability.
        const tThick = targetWall.geometryData.thickness;
        const targetLayers = targetWall.geometryData.layers;

        // Where is the face I stop at?
        // Default: Centerline (0 offset from location line if centered)
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        let _stopDepth = 0;

        // If Target has Core:
        const coreIndex = targetLayers.findIndex(l => l.function === 'Structure');
        if (coreIndex !== -1 && myPriority === 1) {
            // I am Core. Join to Core.
            // Stop at Center of Core? Or Far side of Core? 
            // BIM standard: Core extends to Core.
            // Let's stop at the Location Line (Center) for now = 0.
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            _stopDepth = 0;
        } else {
            // I am Finish or I am Core hitting Finish?
            // If I am Finish, I stop at Exterior Face of Target.
            // Distance to Ext Face = tThick / 2 (assuming centered).
            // If I am on Left, I stop at Left Face.
            // Left Face Offset = tThick/2.
            // Right Face Offset = -tThick/2.

            // Adjust based on side
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            _stopDepth = isLeft ? tThick / 2 : -tThick / 2;
        }

        // 3. Project my vertices onto limits
        // Limit Line:
        // Origin: tStart + stopDepth * perp(tDir)
        // Dir: tDir

        const perp = new THREE.Vector3(-tDir.y, tDir.x, 0);
        // Correct sign for perp? 
        // If isLeft (positive cross), we are on Left. 
        // StopDepth should be positive (Left).
        // If we want the face CLOSEST to us?
        // If I am Finish, I stop at NEAR face.
        // Near Face depth = tThick/2 * (isLeft ? 1 : -1)

        const offsetVector = perp.clone().multiplyScalar(isLeft ? tThick / 2 : -tThick / 2); // Outer Face

        // If Priority 1, simplify to Center (Offset 0)
        const finalOffset = (myPriority === 1) ? new THREE.Vector3(0, 0, 0) : offsetVector;

        const limitOrigin = tStart.clone().add(finalOffset);

        // Intersect my poly edges (Start/End) with Limit Line (limitOrigin, tDir)
        // My edges:
        const leftIdx = isStart ? 0 : 1;
        const rightIdx = isStart ? 3 : 2;

        const newLeft = this.intersectRayLine(poly[leftIdx], myDir, limitOrigin, tDir);
        const newRight = this.intersectRayLine(poly[rightIdx], myDir, limitOrigin, tDir);

        if (newLeft) poly[leftIdx] = newLeft;
        if (newRight) poly[rightIdx] = newRight;

    }



    private static intersectRayLine(p1: THREE.Vector3, d1: THREE.Vector3, p2: THREE.Vector3, d2: THREE.Vector3): THREE.Vector3 | null {
        // Ray 1: P1 + t*D1
        // Line 2: P2 + u*D2
        // 2D intersection
        const det = d1.x * d2.y - d1.y * d2.x;
        // Only true parallelism is rejected: miter points at very acute wall
        // angles legitimately produce small determinants (sin θ between
        // normalized directions), and a wider guard made those corners
        // silently skip their join — the walls met with a visible notch
        // instead of the sharp tip the user expects from Trim/Extend.
        if (Math.abs(det) < 1e-9) return null;

        const dx = p2.x - p1.x;
        const dy = p2.y - p1.y;

        const t = (dx * d2.y - dy * d2.x) / det;
        // u is not needed unless we check bounds

        return p1.clone().add(d1.clone().multiplyScalar(t));
    }

    // =========================================================================
    // 3D SOLID OPERATIONS (BIM 2025 Engine)
    // =========================================================================

    /**
     * Compute the final 3D geometry for this wall, accounting for joins with other walls.
     * Uses SolidKernel (CSG) for precise boolean operations.
     */
    static computeWallSolids(wall: WallElement, connectedWalls: WallElement[]): THREE.Mesh {
        if (!SolidKernel) {
            console.warn("SolidKernel not available");
            return this.createPlaceholderMesh(wall);
        }

        // 1. Generate Base Volume (Unjoined)
        let mainMesh = this.createBaseWallMesh(wall);

        // 2. Iterate intersecting walls
        for (const otherWall of connectedWalls) {
            // Check bounds first
            if (!SolidKernel.intersects(mainMesh, this.createPlaceholderMesh(otherWall))) continue;

            const otherMesh = this.createBaseWallMesh(otherWall);

            // 3. Determine Join Type based on Materials/Priorities
            // Strategy: 
            // - If Materials Match: Union (Join Geometry)
            // - If Different: Subtract the geometric intersection? 
            //   In BIM, strict hierarchy: Structure cuts Finish.

            const myPriority = this.getWallPriority(wall);
            const otherPriority = this.getWallPriority(otherWall);

            if (myPriority > otherPriority) {
                // I am weaker. I get cut by the other wall.
                mainMesh = SolidKernel.subtract(mainMesh, otherMesh);
            } else if (myPriority === otherPriority) {
                // Equal priority.
                // Tie-breaker: ID check
                if (wall.id > otherWall.id) {
                    mainMesh = SolidKernel.subtract(mainMesh, otherMesh);
                }
            }
        }

        return mainMesh;
    }

    private static createBaseWallMesh(wall: WallElement): THREE.Mesh {
        const start = new THREE.Vector3(...wall.geometryData.start);
        const end = new THREE.Vector3(...wall.geometryData.end); // Usually Z is 0 or base elevation?
        // Let's assume start/end are 3D point including Z base elevation?
        // Or 2D projected?
        // In types.ts: WallGeometryData.start: [number, number, number]

        const height = wall.geometryData.height;
        const thickness = wall.geometryData.thickness;

        const dirVector = end.clone().sub(start);
        const length = dirVector.length();
        const center = start.clone().lerp(end, 0.5);

        // Align box with wall rotation
        const geometry = new THREE.BoxGeometry(length, thickness, height);

        const mesh = new THREE.Mesh(geometry);

        // Rotation (around Z)
        const angle = Math.atan2(dirVector.y, dirVector.x);
        mesh.rotation.z = angle;

        // Position
        // Box is centered on creation.
        // Wall Start/End are usually at CENTERLINE or Location Line.
        // If Location Line is not Center, we must offset.
        // We handle Location Line offset in `calculateLayerOffsets` for 2D.
        // For 3D Base Mesh, we should respect it too.
        // But for simplicity of Boolean, let's assume valid Center-to-Center logic for now,
        // or re-use `calculateLayerOffsets` logic later.

        // Center Z:
        // If Start.z is Bottom.
        // Box Center Z = Start.z + Height/2
        const zPos = start.z + height / 2;

        mesh.position.set(center.x, center.y, zPos);

        mesh.updateMatrixWorld();
        return mesh;
    }

    private static createPlaceholderMesh(wall: WallElement): THREE.Mesh {
        // Helper for lightweight bounds check
        return this.createBaseWallMesh(wall);
    }

    private static getWallPriority(wall: WallElement): number {
        // Find lowest priority number (Highest Strength) in layers
        // Default to Finish (5) if no layers
        if (!wall.geometryData.layers || wall.geometryData.layers.length === 0) return 3; // Generic
        return Math.min(...wall.geometryData.layers.map(l => LAYER_PRIORITIES[l.function] || 5));
    }
}
